'use strict';

import { flow, observable, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {GENERIC_ERROR_MESSAGE} from '@uw-it-sis/lib-react/lib/AppConstants';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import { parseJson, isJsonString } from '@uw-it-sis/lib-react/lib/JsonParseUtils';

import api from '../lib/Api';

/**
 * Section syllabus store.
 *
 * This is a caching store.
 *
 */
class CourseSyllabusStore {

    state = States.initial;

    constructor() {
        makeObservable(this, {
            state: observable
        });

        this.syllabuses = {};
        this.exceptionUtils = appStore.getExceptionUtils();
    }

    /**
     * Fetch syllabus HTML content from the syllabi API.
     *
     * @param sectionId - activity id of a course section (e.g. '2018:1:ANTH:469:C')
     */
    loadSyllabus = flow(function* (sectionId) {
         // Update the aggregate state to pending.
        this.state = States.pending;

        // Check to see if store already has the syllabus.
        if (this.haveSyllabus(sectionId)) {
            if (appStore.isDebug()) {
                console.log(sprintf("CourseSyllabusStore: Using cached syllabus data for section [%s].", sectionId));
            }
            this.state = States.done;
            return;
        }

        if (appStore.isDebug()) {
            console.log(sprintf("CourseSyllabusStore: Loading syllabus for section [%s].", sectionId));
        }

        try {
            let response = yield api.getSyllabus(sectionId);
            /*
             * Errors from our controllers are in JSON format. Server errors are usually just text (e.g. HTML).
             * In this case the response is raw HTML on success and JSON on error.
             * Look for a redirect response in the response body, indicating an error.
             */
            if ( isJsonString(response) ) {
                let body = parseJson(response);
                if (body) {
                    if (body.exceptionCode) {
                        throw body;
                    } else {
                        throw {exceptionCode: 500, message: GENERIC_ERROR_MESSAGE}
                    }
                }
            }

            this.syllabuses[sectionId] = response;

            this.state = States.done;
            if (appStore.isDebug()) {
                console.log(sprintf("CourseSyllabusStore: Load complete for section [%s].", sectionId));
            }
        } catch (error) {
            this.exceptionUtils.handleException(error);
            this.state = States.error;
        }
    }.bind(this));

    /**
     * Returns true if a syllabus is available in the store. Otherwise, false.
     * @param sectionId - activity id of a course section (e.g. '2018:1:ANTH:469:C')
     */
    haveSyllabus(sectionId) {
        return sectionId in this.syllabuses;
    }

    /**
     * Returns the syllabus HTML content for a section id.
     * @param sectionId - activity id of a course section (e.g. '2018:1:ANTH:469:C')
     */
    getSyllabus(sectionId) {
        return this.syllabuses[sectionId];
    }
}

export default new CourseSyllabusStore();